import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Copy, Download, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { useApp } from '../context/AppContext';
import { generateNarrative } from '../lib/api';
import { parseNarrativeToHTML } from '../lib/utils';
import { SafeHTML } from '../lib/sanitize';
import { toast } from 'sonner';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHeroCompact } from '../components/layout/PageHeroCompact';
import { getLightSectionTheme } from '../lib/sectionThemes';

export const ReportsPage = () => {
  const navigate = useNavigate();
  const {
    configuration,
    results,
    narrative,
    setNarrative,
    isGeneratingNarrative,
    setIsGeneratingNarrative,
    getSelectedLEIMeasure,
    getSelectedKPIs,
  } = useApp();

  const [isLoading, setIsLoading] = useState(false);
  const selectedLEI = getSelectedLEIMeasure();
  const selectedKPIs = getSelectedKPIs();
  const selectedKPINames = selectedKPIs?.map(k => k.name).join(', ') || 'Selected KPIs';

  if (!results) {
    const __pageThemeEmpty = getLightSectionTheme('Insights');
    return (
      <PageShell theme={__pageThemeEmpty} hero={(
        <PageHeroCompact title={`Reports`} description={`Generate executive reports for stakeholders.`} theme={__pageThemeEmpty} />
      )}>
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-slate-500 mb-4">Run an analysis first to generate narratives</p>
        <Button
          onClick={() => navigate('/configure')}
          className="bg-navy-700 hover:bg-navy-800"
        >
          Configure Analysis
        </Button>
      </div>
      </PageShell>
    );
  }

  const handleGenerateNarrative = async () => {
    setIsLoading(true);
    setIsGeneratingNarrative(true);
    try {
      const response = await generateNarrative({
        lei_measure_name: selectedLEI?.name || 'Selected LEI Measure',
        kpi_name: selectedKPINames,
        results,
        configuration,
      });
      setNarrative(response.data.narrative);
      toast.success('Narrative generated successfully');
    } catch (error) {
      logger.error('Narrative generation error:', error);
      toast.error('Failed to generate narrative');
    } finally {
      setIsLoading(false);
      setIsGeneratingNarrative(false);
    }
  };

  const handleCopyNarrative = () => {
    if (narrative) {
      navigator.clipboard.writeText(narrative);
      toast.success('Narrative copied to clipboard');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const __pageTheme = getLightSectionTheme('Insights');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHeroCompact title={`Reports`} description={`Generate executive reports for stakeholders.`} theme={__pageTheme} />
    )}>
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/results')}
            className="mb-2"
            data-testid="back-to-results-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Results
          </Button>
          <h2 className="text-2xl font-heading font-bold text-slate-900">
            Executive Narrative Report
          </h2>
          <p className="text-slate-500 mt-1">
            AI-generated board-ready analysis narrative
          </p>
        </div>
        <div className="flex gap-2">
          {narrative && (
            <>
              <Button
                variant="outline"
                onClick={handleCopyNarrative}
                data-testid="copy-narrative-btn"
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
              <Button
                variant="outline"
                onClick={handlePrint}
                data-testid="print-report-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                Print/PDF
              </Button>
            </>
          )}
          <Button
            onClick={handleGenerateNarrative}
            disabled={isLoading}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="generate-narrative-btn"
          >
            {isLoading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                {narrative ? 'Regenerate' : 'Generate'} Narrative
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Quick Summary Card */}
      <Card className="border-navy-200 bg-navy-50/30">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Analysis Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-sm text-slate-500">LEI Measure</p>
              <p className="font-semibold text-slate-900">{selectedLEI?.name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Target KPIs</p>
              <p className="font-semibold text-slate-900">
                {selectedKPIs?.length || 0} KPI{selectedKPIs?.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Projected ROI</p>
              <p className="font-semibold text-teal-600">
                {((results.roi_percent || results.total_roi_percent || 0) / 100).toFixed(1)}x
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500">
                {results.roi_p5 !== undefined ? 'Confidence Range' : 'Financial Impact'}
              </p>
              <p className="font-semibold text-slate-900">
                {results.roi_p5 !== undefined 
                  ? `${(results.roi_p5 / 100).toFixed(1)}x - ${(results.roi_p95 / 100).toFixed(1)}x`
                  : `$${Math.round(results.total_financial_impact || 0).toLocaleString()}`
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Narrative Content */}
      {narrative ? (
        <Card className="print:shadow-none print:border-0">
          <CardHeader className="print:pb-2">
            <CardTitle className="text-xl font-heading flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-teal-600" />
              Executive Narrative
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SafeHTML
              className="prose prose-slate max-w-none"
              html={parseNarrativeToHTML(narrative)}
            />
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-16">
            <div className="text-center">
              <Sparkles className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No Narrative Generated</h3>
              <p className="text-slate-500 mt-2 max-w-md mx-auto">
                Click "Generate Narrative" to create an AI-powered executive summary 
                with causal breakdown, risk analysis, and strategic recommendations.
              </p>
              <Button
                onClick={handleGenerateNarrative}
                disabled={isLoading}
                className="mt-6 bg-teal-600 hover:bg-teal-700"
                data-testid="generate-first-narrative-btn"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Narrative
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Structured Sections (Always visible) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:hidden">
        {/* Causal Components */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">Causal Components</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Influence Weight</p>
                  <p className="text-sm text-slate-500">Directional strength of LEI-KPI relationship</p>
                </div>
                <p className="text-xl font-bold text-navy-700">
                  {(results.weight || results.kpi_results?.[0]?.weight || 0.95).toFixed(2)}
                </p>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Elasticity</p>
                  <p className="text-sm text-slate-500">Marginal sensitivity coefficient</p>
                </div>
                <p className="text-xl font-bold text-teal-600">
                  {(results.elasticity || results.kpi_results?.[0]?.elasticity || 0.80).toFixed(2)}
                </p>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Dampening Factor</p>
                  <p className="text-sm text-slate-500">Execution realism adjustment</p>
                </div>
                <p className="text-xl font-bold text-amber-600">
                  {(results.dampening || 0.74).toFixed(2)}
                </p>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Maturity Factor</p>
                  <p className="text-sm text-slate-500">Diminishing returns adjustment</p>
                </div>
                <p className="text-xl font-bold text-slate-700">
                  {(results.maturity_factor || 1.00).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Risk Profile - only show Monte Carlo data if available */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">
              {results.roi_p5 !== undefined ? 'Risk Profile' : 'Impact Summary'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {results.roi_p5 !== undefined ? (
              // Monte Carlo risk profile (old single-KPI format)
              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm text-emerald-800 font-medium">Upside Scenario (P95)</p>
                  <p className="text-2xl font-bold text-emerald-600">{(results.roi_p95 / 100).toFixed(1)}x ROI</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-slate-600 font-medium">Base Case (Mean)</p>
                  <p className="text-2xl font-bold text-slate-900">{(results.roi_mean / 100).toFixed(1)}x ROI</p>
                </div>
                <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
                  <p className="text-sm text-rose-800 font-medium">Downside Scenario (P5)</p>
                  <p className="text-2xl font-bold text-rose-600">{(results.roi_p5 / 100).toFixed(1)}x ROI</p>
                </div>
                <div className="text-center pt-2">
                  <p className="text-sm text-slate-500">
                    Probability of positive ROI: <span className="font-bold text-teal-600">{results.prob_positive_roi?.toFixed(1)}%</span>
                  </p>
                </div>
              </div>
            ) : (
              // Multi-KPI summary (new format)
              <div className="space-y-4">
                <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <p className="text-sm text-teal-800 font-medium">Total KPI Uplift</p>
                  <p className="text-2xl font-bold text-teal-600">
                    {(results.total_kpi_delta_percent || 0).toFixed(2)}%
                  </p>
                </div>
                <div className="p-4 bg-navy-50 rounded-lg border border-navy-200">
                  <p className="text-sm text-navy-800 font-medium">Total Financial Impact</p>
                  <p className="text-2xl font-bold text-navy-700">
                    ${Math.round(results.total_financial_impact || 0).toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm text-emerald-800 font-medium">Net Benefit</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    ${Math.round(results.total_net_benefit || 0).toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-800 font-medium">Return on Investment</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {((results.total_roi_percent || 0) / 100).toFixed(1)}x
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </PageShell>
  );
};

export default ReportsPage;
